import React, { useEffect, useState } from "react";
import { useWebData } from "../context/WebDataContext"; 
import { db } from "../firebaseConfig";
import { doc, updateDoc, deleteDoc } from "firebase/firestore"; 
import * as XLSX from "xlsx";
import Loader from "../components/Loader";

function Leaves() {
  // 🔥 Context se Data
  const { leaves, users, loading, isAdmin, isAccount, currentUser } = useWebData();

  // 🔥 Filter States
  const [viewMode, setViewMode] = useState('Month'); 
  const [selectedDate, setSelectedDate] = useState(new Date()); 
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); 
  const [searchTerm, setSearchTerm] = useState("");
  const [filtered, setFiltered] = useState([]);

  // States
  const [viewData, setViewData] = useState(null);
  const [editData, setEditData] = useState(null);
  const [showBalance, setShowBalance] = useState(false);
  const [balanceList, setBalanceList] = useState([]);

  const TOTAL_LEAVES_ALLOWED = 21;

  // ==========================================
  // 1. CALCULATE BALANCE (🔥 Fixed Logic for Mixed Dates)
  // ==========================================
  useEffect(() => {
    if(!users || !leaves) return;

    const usageMap = {};
    const targetYear = parseInt(selectedYear); // Dropdown Year

    leaves.forEach(leave => {
        if(leave.status === 'Approved') {
            
            // 🔥 DATE PARSING FIX (Manual Check)
            const rawDate = leave.fromDate || "";
            let leaveYear = 0;

            // Try Standard Date Object
            const d = new Date(rawDate);
            if (!isNaN(d.getTime())) {
                leaveYear = d.getFullYear();
            } 
            // Try Manual Parse (DD-MM-YYYY or YYYY-MM-DD)
            else {
                const parts = rawDate.split(/[-/]/);
                if (parts.length === 3) {
                    if (parts[2].length === 4) leaveYear = parseInt(parts[2]); // 25-01-2025
                    else if (parts[0].length === 4) leaveYear = parseInt(parts[0]); // 2025-01-25
                }
            }

            // Check if Year Matches
            if(leaveYear === targetYear) {
                const name = leave.userName;
                const days = parseFloat(leave.days) || 0;
                usageMap[name] = (usageMap[name] || 0) + days;
            }
        }
    });

    // Merge with Users
    const report = users.map(user => {
        const taken = usageMap[user.name] || 0;
        return {
            id: user.id,
            name: user.name,
            role: user.role || "Employee",
            taken: taken,
            balance: TOTAL_LEAVES_ALLOWED - taken
        };
    });

    setBalanceList(report);

  }, [leaves, users, selectedYear]); 

  // ==========================================
  // 2. UNIVERSAL FILTER LOGIC
  // ==========================================
  useEffect(() => {
    if (!leaves) return;

    let result = [...leaves];

    // Search
    if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        result = result.filter(l => {
            const dateStr = l.createdAt ? new Date(l.createdAt).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric'
            }).toLowerCase() : "";
            const row = `${l.userName} ${l.type} ${l.reason} ${l.status}`.toLowerCase();
            return row.includes(lower) || dateStr.includes(lower);
        });
    }

    // Date Filter
    if (viewMode !== 'All') {
        const targetYear = viewMode === 'Year' ? parseInt(selectedYear) : selectedDate.getFullYear();
        const targetMonth = selectedDate.getMonth(); 

        result = result.filter(item => {
            const rawDate = item.createdAt || ""; 
            if (!rawDate) return false;

            let y = 0; let m = -1;
            const d = new Date(rawDate);
            if (!isNaN(d.getTime())) {
                y = d.getFullYear(); m = d.getMonth();
            } else {
                const parts = rawDate.split(/[-/]/);
                if (parts.length === 3) {
                    if (parts[2].length === 4) { y = parseInt(parts[2]); m = parseInt(parts[1]) - 1; }
                    else if (parts[0].length === 4) { y = parseInt(parts[0]); m = parseInt(parts[1]) - 1; }
                }
            }

            if (viewMode === 'Year') return y === targetYear;
            if (viewMode === 'Month') return y === targetYear && m === targetMonth;
            return true;
        });
    }

    result.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    setFiltered(result);
  }, [leaves, searchTerm, viewMode, selectedDate, selectedYear]); 

  // ==========================================
  // 3. EXCEL DOWNLOAD
  // ==========================================
  const downloadExcel = () => {
    const dataToExport = filtered.map(i => ({
        Req_Date: formatDate(i.createdAt),
        Employee: i.userName,
        Leave_Type: i.type,
        From_Date: i.fromDate,
        To_Date: i.toDate,
        Total_Days: i.days,
        Reason: i.reason,
        Status: i.status,
        Approved_By: i.approvedBy || "-",
        Approved_At: formatDate(i.approvedAt)
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Leaves");
    XLSX.writeFile(wb, `Leaves_Report_${viewMode}.xlsx`);
  };

  // ACTIONS
  const updateStatus = async (id, status) => {
      if(window.confirm(`Mark request as ${status}?`)) {
          try {
              await updateDoc(doc(db, "leaves", id), { 
                  status,
                  approvedBy: currentUser?.name || "Admin",
                  approvedAt: new Date().toISOString()
              });
              if(viewData) setViewData(null);
          } catch(e) { alert("Error updating status"); }
      }
  };

  const handleSaveEdit = async () => {
      if(!editData) return;
      try {
          await updateDoc(doc(db, "leaves", editData.id), {
              fromDate: editData.fromDate,
              toDate: editData.toDate,
              days: editData.days,
              type: editData.type,
              reason: editData.reason,
              status: editData.status
          });
          alert("Leave Updated ✅");
          setEditData(null);
      } catch (e) { alert("Error updating"); }
  };

  const handleDelete = async (id) => {
      if(window.confirm("Delete this request?")) await deleteDoc(doc(db, "leaves", id));
  };

  // Helpers
  const handleMonthChange = (dir) => {
      const d = new Date(selectedDate);
      d.setMonth(d.getMonth() + dir);
      setSelectedDate(d);
  };

  const getYearOptions = () => {
      const currentY = new Date().getFullYear();
      const years = [];
      for(let y = 2024; y <= currentY + 5; y++) years.push(y);
      return years;
  };

  const formatDate = (iso) => {
      if(!iso) return "-";
      const date = new Date(iso);
      return isNaN(date.getTime()) ? iso : date.toLocaleDateString('en-GB');
  };

  return (
    <div style={{padding:"20px", fontFamily:"'Segoe UI', sans-serif", backgroundColor:"#f8f9fa", minHeight:"100vh"}}>
      
      {/* HEADER */}
      <div style={headerCard}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px"}}>
            <h2 style={{margin:0, color:"#3b5998", display:"flex", alignItems:"center"}}>
                📝 Leave Requests <span style={{fontSize:"14px", color:"#777", marginLeft:"10px"}}>({filtered.length})</span>
            </h2>
            <div style={{display:"flex", gap:"10px"}}>
                <button onClick={() => setShowBalance(true)} style={btnBalance}>📊 Check Balance ({selectedYear})</button>
                <button onClick={downloadExcel} style={btnDownload}>📥 Download Excel</button>
            </div>
        </div>

        <div style={{display:"flex", gap:"10px", flexWrap:"wrap", alignItems:"center", justifyContent:"space-between"}}>
            
            <div style={{display:"flex", gap:"5px", backgroundColor:"#eee", padding:"4px", borderRadius:"6px"}}>
                {['Month', 'Year', 'All'].map(mode => (
                    <button key={mode} onClick={()=>setViewMode(mode)} 
                        style={{...toggleBtn, backgroundColor: viewMode===mode ? "white" : "transparent", color: viewMode===mode ? "#333" : "#777", boxShadow: viewMode===mode ? "0 2px 5px rgba(0,0,0,0.1)" : "none"}}>
                        {mode}
                    </button>
                ))}
            </div>

            {viewMode === 'Month' && (
                <div style={{display:"flex", alignItems:"center", gap:"10px", backgroundColor:"white", padding:"5px 15px", borderRadius:"6px", border:"1px solid #ddd"}}>
                    <button onClick={()=>handleMonthChange(-1)} style={navBtn}>◀</button>
                    <span style={{fontWeight:"bold", color:"#333", minWidth:"120px", textAlign:"center"}}>
                        {selectedDate.toLocaleDateString('en-US', {month:'long', year:'numeric'})}
                    </span>
                    <button onClick={()=>handleMonthChange(1)} style={navBtn}>▶</button>
                </div>
            )}

            {/* 🔥 IMPORTANT: This dropdown controls Balance Year too */}
            {viewMode === 'Year' && (
                <select 
                    value={selectedYear} 
                    onChange={(e) => setSelectedYear(e.target.value)}
                    style={{padding:"8px", borderRadius:"6px", border:"1px solid #ddd", fontWeight:"bold", fontSize:"14px", cursor:"pointer"}}
                >
                    {getYearOptions().map(y => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
            )}

            <input type="text" placeholder="🔍 Search Name, Reason, Type..." value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} style={searchInput} />
        </div>
      </div>

      {loading ? <Loader /> : (
        <div style={{overflowX:"auto", backgroundColor:"white", borderRadius:"8px", boxShadow:"0 2px 10px rgba(0,0,0,0.05)"}}>
          <table style={tableStyle}>
            <thead>
                <tr style={{backgroundColor:"#3b5998", color:"white", textAlign:"left"}}>
                    <th style={thStyle}>Req Date</th>
                    <th style={thStyle}>Employee</th>
                    <th style={thStyle}>From</th>
                    <th style={thStyle}>To</th>
                    <th style={thStyle}>Days</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Actions</th>
                </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                  <tr><td colSpan="8" style={{padding:"20px", textAlign:"center", color:"#999"}}>No requests found.</td></tr>
              ) : filtered.map(row => (
                <tr key={row.id} style={{borderBottom:"1px solid #eee", height:"45px"}}>
                    <td style={tdStyle}>{formatDate(row.createdAt)}</td>
                    <td style={{...tdStyle, fontWeight:"bold", color:"#2c3e50"}}>{row.userName}</td>
                    <td style={tdStyle}>{row.fromDate}</td>
                    <td style={tdStyle}>{row.toDate}</td>
                    <td style={{...tdStyle, fontWeight:"bold"}}>{row.days}</td>
                    <td style={tdStyle}>
                        <span style={{padding:"2px 8px", borderRadius:"10px", backgroundColor:"#e8eaf6", fontSize:"11px", color:"#3b5998"}}>{row.type}</span>
                    </td>
                    <td style={tdStyle}>
                        <span style={{
                            padding:"4px 10px", borderRadius:"12px", fontSize:"11px", fontWeight:"bold",
                            backgroundColor: row.status==='Approved'?'#e8f5e9':(row.status==='Rejected'?'#ffebee':'#fff3e0'),
                            color: row.status==='Approved'?'green':(row.status==='Rejected'?'red':'orange')
                        }}>
                            {row.status}
                        </span>
                    </td>
                    <td style={tdStyle}>
                        <div style={{display:"flex", gap:"3px"}}>
                            {row.status === 'Pending' && (isAdmin || isAccount) && (
                                <>
                                    <button onClick={() => updateStatus(row.id, 'Approved')} style={btnIconCheck} title="Approve">✔</button>
                                    <button onClick={() => updateStatus(row.id, 'Rejected')} style={btnIconCross} title="Reject">✖</button>
                                </>
                            )}
                            <button onClick={() => setViewData(row)} style={btnIconEye} title="View">👁️</button>
                            {(isAdmin || isAccount) && (
                                <>
                                    <button onClick={() => setEditData(row)} style={btnIconEdit} title="Edit">✏️</button>
                                    <button onClick={() => handleDelete(row.id)} style={btnIconDelete} title="Delete">🗑️</button>
                                </>
                            )}
                        </div>
                    </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* VIEW MODAL */}
      {viewData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <h3 style={{color:"#3b5998", marginTop:0}}>📝 Leave Request</h3>
            <div style={{fontSize:"13px", lineHeight:"1.6"}}>
                <p><strong>📅 Req Date:</strong> {formatDate(viewData.createdAt)}</p>
                <p><strong>👤 Employee:</strong> {viewData.userName}</p>
                <div style={{background:"#f0f4ff", padding:"10px", borderRadius:"5px", margin:"10px 0"}}>
                    <p style={{margin:0}}><strong>🗓️ Duration:</strong> {viewData.fromDate} to {viewData.toDate}</p>
                    <p style={{margin:"5px 0 0 0"}}><strong>⏳ Total Days:</strong> {viewData.days}</p>
                </div>
                <p><strong>🏷️ Type:</strong> {viewData.type}</p>
                <p><strong>📝 Reason:</strong> {viewData.reason}</p>
                <p><strong>🔥 Status:</strong> {viewData.status}</p>
            </div>
            {viewData.status === 'Pending' && (isAdmin || isAccount) && (
                <div style={{marginTop:"15px", display:"flex", gap:"10px"}}>
                    <button onClick={()=>updateStatus(viewData.id, 'Approved')} style={btnBigApprove}>Approve</button>
                    <button onClick={()=>updateStatus(viewData.id, 'Rejected')} style={btnBigReject}>Reject</button>
                </div>
            )}
            <button onClick={() => setViewData(null)} style={btnClose}>Close</button>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {editData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <h3 style={{color:"#f39c12", marginTop:0}}>✏️ Edit Leave</h3>
            <div style={{display:"flex", gap:"10px"}}>
                <div style={{flex:1}}><label style={labelStyle}>From</label><input type="date" value={editData.fromDate} onChange={(e)=>setEditData({...editData, fromDate:e.target.value})} style={modalInput}/></div>
                <div style={{flex:1}}><label style={labelStyle}>To</label><input type="date" value={editData.toDate} onChange={(e)=>setEditData({...editData, toDate:e.target.value})} style={modalInput}/></div>
            </div>
            <label style={labelStyle}>Days</label><input type="number" value={editData.days} onChange={(e)=>setEditData({...editData, days:e.target.value})} style={modalInput}/>
            <label style={labelStyle}>Reason</label><input type="text" value={editData.reason} onChange={(e)=>setEditData({...editData, reason:e.target.value})} style={modalInput}/>
            <label style={labelStyle}>Status</label>
            <select value={editData.status} onChange={(e)=>setEditData({...editData, status:e.target.value})} style={modalInput}>
                <option value="Pending">Pending</option><option value="Approved">Approved</option><option value="Rejected">Rejected</option>
            </select>
            <div style={{marginTop:"15px", textAlign:"right"}}>
                <button onClick={()=>setEditData(null)} style={btnCancel}>Cancel</button>
                <button onClick={handleSaveEdit} style={btnSave}>Update</button>
            </div>
          </div>
        </div>
      )}

      {/* BALANCE MODAL */}
      {showBalance && (
        <div style={modalOverlay}>
            <div style={modalContent}>
                <h3 style={{color:"#e67e22", marginTop:0, borderBottom:"1px solid #eee", paddingBottom:"10px"}}>📊 Leave Balance ({selectedYear})</h3>
                <p style={{fontSize:"12px", color:"gray", marginBottom:"10px"}}>Total Allowed: {TOTAL_LEAVES_ALLOWED} Days / Year</p>
                <div style={{maxHeight:"300px", overflowY:"auto"}}>
                    <table style={{width:"100%", fontSize:"13px", borderCollapse:"collapse"}}>
                        <thead>
                            <tr style={{background:"#f5f5f5"}}>
                                <th style={{padding:"8px", textAlign:"left"}}>Employee</th>
                                <th style={{padding:"8px", textAlign:"center"}}>Taken</th>
                                <th style={{padding:"8px", textAlign:"center"}}>Bal</th>
                            </tr>
                        </thead>
                        <tbody>
                            {balanceList.length === 0 ? (
                                <tr><td colSpan="3" style={{padding:"10px", textAlign:"center", color:"#999"}}>No users found.</td></tr>
                            ) : balanceList.map(user => (
                                <tr key={user.id} style={{borderBottom:"1px solid #eee"}}>
                                    <td style={{padding:"8px"}}>
                                        {user.name} 
                                        <div style={{fontSize:"10px", color:"gray"}}>{user.role}</div>
                                    </td>
                                    <td style={{padding:"8px", textAlign:"center", color:"red", fontWeight:"bold"}}>{user.taken}</td>
                                    <td style={{padding:"8px", textAlign:"center", color:"green", fontWeight:"bold"}}>{user.balance}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <button onClick={() => setShowBalance(false)} style={btnClose}>Close</button>
            </div>
        </div>
      )}
    </div>
  );
}

// STYLES
const headerCard={backgroundColor:"white", padding:"20px", borderRadius:"10px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)", marginBottom:"20px"};
const tableStyle={width:"100%", borderCollapse:"collapse", fontSize:"13px"};
const thStyle={padding:"12px 15px", borderBottom:"2px solid #eee", fontSize:"12px", textTransform:"uppercase", letterSpacing:"0.5px"};
const tdStyle={padding:"10px 15px", verticalAlign:"middle"};
const toggleBtn={border:"none", padding:"6px 12px", borderRadius:"5px", cursor:"pointer", fontSize:"13px", fontWeight:"600", transition:"0.2s"};
const navBtn={background:"none", border:"none", cursor:"pointer", fontSize:"16px", color:"#555"};
const searchInput={padding:"8px 12px", border:"1px solid #ddd", borderRadius:"6px", width:"200px", outline:"none"};
const btnDownload={backgroundColor:"#27ae60", color:"white", border:"none", padding:"8px 15px", borderRadius:"5px", cursor:"pointer", fontWeight:"bold", fontSize:"13px"};
const btnBalance={backgroundColor:"#e67e22", color:"white", border:"none", padding:"8px 15px", borderRadius:"5px", cursor:"pointer", fontWeight:"bold", fontSize:"13px"};

const btnIconCheck={backgroundColor:"#e8f5e9", color:"green", border:"1px solid #c8e6c9", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconCross={backgroundColor:"#ffebee", color:"red", border:"1px solid #ffcdd2", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconEye={backgroundColor:"#e3f2fd", color:"#1976d2", border:"1px solid #bbdefb", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconDelete={backgroundColor:"#fff", color:"#777", border:"1px solid #ddd", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconEdit={backgroundColor:"#fff3e0", color:"#e67e22", border:"1px solid #ffe0b2", padding:"5px 8px", borderRadius:"4px", cursor:"pointer"};
const btnBigApprove={flex:1, padding:"12px", backgroundColor:"#27ae60", color:"white", border:"none", borderRadius:"5px", cursor:"pointer", fontWeight:"bold"};
const btnBigReject={flex:1, padding:"12px", backgroundColor:"#c0392b", color:"white", border:"none", borderRadius:"5px", cursor:"pointer", fontWeight:"bold"};
const btnSave={backgroundColor:"#3b5998", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer", marginLeft:"5px"};
const btnCancel={backgroundColor:"#95a5a6", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer"};
const btnClose={marginTop:"15px", backgroundColor:"#333", color:"white", border:"none", padding:"8px", borderRadius:"4px", width:"100%", cursor:"pointer"};

const modalOverlay={position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000};
const modalContent={background:"white", padding:"25px", borderRadius:"12px", width:"400px", boxShadow:"0 10px 30px rgba(0,0,0,0.2)", position:"relative"};
const modalInput={width:"100%", padding:"8px", margin:"5px 0", borderRadius:"4px", border:"1px solid #ccc", boxSizing:"border-box", fontSize:"13px"};
const labelStyle={fontSize:"11px", fontWeight:"bold", color:"#555"};

export default Leaves;